import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import type React from "react";
import { theme, card, emptyFrame, btnPrimary, inputStyle } from "../theme";
import { LoadingBlock } from "../components/Spinner";
import { api, ApiError } from "../api/client";

import { cbSafe } from "../lib/colourBlind";
const GRADES = [
  { id: "senior",     description: "Strategic advisory, architecture, senior risk reviews" },
  { id: "consultant", description: "Control implementation, documentation, gap analysis" },
  { id: "junior",     description: "Evidence gathering, template population, admin support" },
] as const;

type GradeId = (typeof GRADES)[number]["id"];

// Grade rank: higher = more senior. A consultant can work at their rank or below.
const GRADE_RANK: Record<string, number> = { senior: 3, consultant: 2, junior: 1 };

interface RateEntry { dayPence: number; hourPence: number; label: string; }

interface SlotAvailability {
  totalHours: number;
  bookedHours: number;
  remainingHours: number;
}

interface ConsultantSummary {
  id: string;
  displayName: string;
  primaryGrade: string | null;
}

interface Slot {
  id: string;
  grade: string;
  slotDate: string;
  hours: number;
  note: string | null;
  status: string;
  costPence: number;
  createdAt: string;
}

interface SlotsResponse {
  slots: Slot[];
  // availability[dateKey][consultantId] = { totalHours, bookedHours, remainingHours }
  availability: Record<string, Record<string, SlotAvailability>>;
  /** Days the consultancy is closed: nobody can be booked (backend lib/consultantCapacity.ts). */
  holidays?: Array<{ date: string; reason: string | null }>;
  consultants: ConsultantSummary[];
  rateCard?: Record<string, RateEntry>;
  hasConsultancy: boolean;
  capacitySet: boolean;
  billingMode?: string;
  managedServiceRatePence?: number | null;
  managedServiceGrade?: string | null;
  managedServiceDaysPerMonth?: number;
  managedServiceHoursPerMonth?: number;
}

const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const DAY_LABELS = ["Mo","Tu","We","Th","Fr","Sa","Su"];

function fmt(pence: number) {
  return `£${(pence / 100).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function pad2(n: number) { return String(n).padStart(2, "0"); }

function toDateKey(date: Date) {
  return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`;
}

function todayKey() {
  return toDateKey(new Date());
}

function slotStatusStyle(status: string): { color: string; label: string } {
  if (status === "confirmed") return { color: theme.color.successText, label: "Confirmed" };
  if (status === "pending")   return { color: theme.color.warningText, label: "Awaiting confirmation" };
  if (status === "declined")  return { color: theme.color.dangerText,  label: "Declined" };
  if (status === "cancelled") return { color: theme.color.faint,       label: "Cancelled" };
  return { color: theme.color.muted, label: status };
}

function hoursLabel(hours: number) {
  if (hours % 8 === 0) {
    const d = hours / 8;
    return `${d} day${d !== 1 ? "s" : ""}`;
  }
  return `${hours} hour${hours !== 1 ? "s" : ""}`;
}

// ── Secondary button style ────────────────────────────────────────────────

const secondaryBtn: React.CSSProperties = {
  padding: "7px 14px",
  borderRadius: 3,
  border: `1px solid ${theme.color.borderStrong}`,
  background: "transparent",
  color: theme.color.muted,
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
};

// ── Main page ─────────────────────────────────────────────────────────────

export function ConsultancyTimePage() {
  const now = new Date();
  // ?date=YYYY-MM-DD (from the Schedule page's day panel) opens that month and day.
  const [searchParams] = useSearchParams();
  const initialDate = ((d) => (d && /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : null))(searchParams.get("date"));
  const [monthOffset, setMonthOffset] = useState(() => initialDate
    ? (Number(initialDate.slice(0, 4)) - now.getUTCFullYear()) * 12 + (Number(initialDate.slice(5, 7)) - 1 - now.getUTCMonth())
    : 0);
  const [data, setData] = useState<SlotsResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(initialDate);

  // Booking form
  const [bookingConsultantId, setBookingConsultantId] = useState<string | null>(null);
  const [bookingGrade, setBookingGrade] = useState<GradeId>("senior");
  const [bookingType, setBookingType]   = useState<"day" | "hour">("day");
  const [bookingQty, setBookingQty]     = useState(1);
  const [bookingNote, setBookingNote]   = useState("");
  const [submitting, setSubmitting]     = useState(false);
  const [submitError, setSubmitError]   = useState("");
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const viewYear = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + monthOffset, 1)).getUTCFullYear();
  const viewMon  = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + monthOffset, 1)).getUTCMonth() + 1;
  const monthStr = `${viewYear}-${pad2(viewMon)}`;

  const load = useCallback(async () => {
    try {
      const d = await api.get<SlotsResponse>(`/billing/slots?month=${monthStr}`);
      setData(d);
      setLoadError(null);
    } catch (e) {
      setLoadError(e instanceof Error && e.message ? e.message : "Could not load availability");
    }
  }, [monthStr]);

  useEffect(() => { load(); }, [load]);

  // A package that includes consultancy hours (the Jersey package, GET /usage jerseyPackage): said
  // above the rate card, so an included booking is not read as chargeable (AUD-28).
  const [includedHours, setIncludedHours] = useState<{ thisYear: number; byYear: number[] } | null>(null);
  useEffect(() => {
    api.get<{ jerseyPackage?: { consultancyHoursThisYear: number; consultancyHoursByYear: number[] } | null }>("/usage")
      .then((u) => setIncludedHours(u.jerseyPackage ? { thisYear: u.jerseyPackage.consultancyHoursThisYear, byYear: u.jerseyPackage.consultancyHoursByYear } : null))
      .catch(() => setIncludedHours(null));
  }, []);

  // Index own slots by date
  const mySlotsByDate: Record<string, Slot[]> = {};
  for (const s of data?.slots ?? []) {
    const d = s.slotDate.slice(0, 10);
    (mySlotsByDate[d] ??= []).push(s);
  }

  function remainingHoursForConsultant(dateKey: string, consultantId: string): number {
    return data?.availability?.[dateKey]?.[consultantId]?.remainingHours ?? 0;
  }

  function hasAnyCapacity(dateKey: string): boolean {
    const avail = data?.availability?.[dateKey];
    if (!avail) return false;
    return Object.values(avail).some((c) => c.remainingHours > 0);
  }

  // Grades eligible for a given consultant (at or below their primary grade)
  function eligibleGrades(consultant: ConsultantSummary): GradeId[] {
    const rank = GRADE_RANK[consultant.primaryGrade ?? ""] ?? 0;
    return GRADES.filter((g) => (GRADE_RANK[g.id] ?? 0) <= rank).map((g) => g.id);
  }

  // ── Calendar grid ─────────────────────────────────────────────────────

  const daysInMonth = new Date(viewYear, viewMon, 0).getDate();
  const firstDay = new Date(Date.UTC(viewYear, viewMon - 1, 1));
  const startOffset = (firstDay.getUTCDay() + 6) % 7; // 0=Mon … 6=Sun

  const cells: Array<string | null> = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(`${monthStr}-${pad2(d)}`);
  }
  while (cells.length % 7 !== 0) cells.push(null);

  function openDate(dateKey: string) {
    if (selectedDate === dateKey) { setSelectedDate(null); return; }
    setSelectedDate(dateKey);
    // Reset form — consultant picker comes first, no auto-selection
    setBookingConsultantId(null);
    setBookingGrade("senior");
    setBookingType("day");
    setBookingQty(1);
    setBookingNote("");
    setSubmitError("");
    setSubmitSuccess(false);
  }

  function selectConsultant(consultantId: string) {
    setBookingConsultantId(consultantId);
    // Auto-select highest eligible grade for this consultant
    const consultant = (data?.consultants ?? []).find((c) => c.id === consultantId);
    if (consultant) {
      const grades = eligibleGrades(consultant);
      setBookingGrade(grades[0] ?? "junior");
    }
    setBookingType("day");
    setBookingQty(1);
    setSubmitError("");
  }

  async function submitBooking() {
    if (!selectedDate || !bookingConsultantId) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      await api.post("/billing/slots", {
        consultantId: bookingConsultantId,
        grade:        bookingGrade,
        date:         selectedDate,
        type:         bookingType,
        quantity:     bookingQty,
        note:         bookingNote.trim() || undefined,
      });
      setSelectedDate(null);
      setBookingConsultantId(null);
      setSubmitSuccess(true);
      await load();
    } catch (e) {
      setSubmitError(e instanceof ApiError ? e.message : "Could not submit request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function cancelSlot(id: string) {
    try {
      await api.del(`/billing/slots/${id}`);
      await load();
    } catch { /* non-fatal */ }
  }

  // ── Derived slot lists ────────────────────────────────────────────────

  const today = todayKey();
  const pendingSlots     = (data?.slots ?? []).filter((s) => s.status === "pending");
  const confirmedUpcoming = (data?.slots ?? []).filter((s) => s.status === "confirmed" && s.slotDate.slice(0, 10) >= today);
  const historySlots     = (data?.slots ?? []).filter((s) => s.status !== "pending" && !(s.status === "confirmed" && s.slotDate.slice(0, 10) >= today));

  const selectedConsultant = (data?.consultants ?? []).find((c) => c.id === bookingConsultantId) ?? null;
  const gradeRate = data?.rateCard?.[bookingGrade];
  const estimatedCostPence = gradeRate
    ? (bookingType === "day" ? bookingQty * gradeRate.dayPence : bookingQty * gradeRate.hourPence)
    : 0;
  const maxQty = bookingType === "day" ? 5 : 8;

  // ── Managed service allotment ─────────────────────────────────────────
  const isManagedService = data?.billingMode === "managed_service";
  const msAllotmentHours = ((data?.managedServiceDaysPerMonth ?? 0) * 8) + (data?.managedServiceHoursPerMonth ?? 0);
  const msBookedHours    = (data?.slots ?? [])
    .filter((s) => s.status === "pending" || s.status === "confirmed")
    .reduce((sum, s) => sum + s.hours, 0);
  const msRemainingHours = Math.max(0, msAllotmentHours - msBookedHours);
  const msRemainingDays  = Math.floor(msRemainingHours / 8);
  const msRemainingHrsRemainder = msRemainingHours % 8;

  // ── Early returns ─────────────────────────────────────────────────────

  if (!data) {
    if (loadError) return <div style={{ ...card, color: theme.color.dangerText, fontSize: 13 }}>{loadError}</div>;
    return <LoadingBlock />;
  }

  if (!data.hasConsultancy) {
    return (
      <div className="page-pad">
        <h1 style={{ margin: "0 0 16px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>Book Consultancy Time</h1>
        <div style={{ ...card, fontSize: 13, color: theme.color.subtle }}>
          Your organisation does not have a consultancy assigned. Contact your account manager to get set up.
        </div>
      </div>
    );
  }

  if (!data.capacitySet) {
    return (
      <div className="page-pad">
        <h1 style={{ margin: "0 0 16px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>Book Consultancy Time</h1>
        <div style={{ ...card, fontSize: 13, color: theme.color.subtle }}>
          Your consultant has not yet published their availability. Please check back soon or contact them directly.
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────

  // Consultancy days allocated but no individual consultant slots set up yet:
  // nothing can be booked, but the organisation's own bookings still show.
  const bookable = data.consultants.length > 0;

  return (
    <div className="page-pad">
      <h1 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>
        Book Consultancy Time
      </h1>
      {bookable && (
        <p style={{ margin: "0 0 20px", fontSize: 13, color: theme.color.subtle, lineHeight: 1.6 }}>
          Pick a date from the calendar to request a day or hour slot. Your consultant will confirm the booking.
        </p>
      )}

      {!bookable && (
        <div style={{ ...card, margin: "14px 0 20px", fontSize: 13, color: theme.color.subtle }}>
          Your consultant has configured your allocation but has not yet set up individual booking slots. Please contact them directly to schedule time, or check back soon.
        </div>
      )}


      {/* ── Managed service allotment summary ── */}
      {isManagedService && (
        <div style={{ ...card, marginBottom: 20, padding: "12px 18px", background: theme.color.accentBg, border: `1px solid ${theme.color.accent}44` }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.accentText, marginBottom: 6 }}>
            Managed service allotment — {MONTH_NAMES[viewMon - 1]} {viewYear}
          </div>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 800, color: theme.color.accentText }}>
                {msRemainingDays > 0 || msRemainingHrsRemainder > 0
                  ? [msRemainingDays > 0 ? `${msRemainingDays}d` : "", msRemainingHrsRemainder > 0 ? `${msRemainingHrsRemainder}h` : ""].filter(Boolean).join(" ")
                  : "0h"}
              </div>
              <div style={{ fontSize: 11, color: theme.color.accentText, opacity: 0.75 }}>remaining this month</div>
            </div>
            <div>
              <div style={{ fontSize: 20, fontWeight: 800, color: theme.color.accentText }}>{msBookedHours >= 8 ? `${Math.floor(msBookedHours / 8)}d ${msBookedHours % 8 > 0 ? `${msBookedHours % 8}h` : ""}`.trim() : `${msBookedHours}h`}</div>
              <div style={{ fontSize: 11, color: theme.color.accentText, opacity: 0.75 }}>booked of {msAllotmentHours >= 8 ? `${Math.floor(msAllotmentHours / 8)}d ${msAllotmentHours % 8 > 0 ? `${msAllotmentHours % 8}h` : ""}`.trim() : `${msAllotmentHours}h`} allotment</div>
            </div>
          </div>
        </div>
      )}

      {submitSuccess && (
        <div style={{ marginBottom: 16, padding: "12px 16px", background: cbSafe("#052e1644"), border: `1px solid ${theme.color.success}`, borderRadius: 3, fontSize: 13, color: theme.color.successText, fontWeight: 600 }}>
          Booking request sent — your consultant has been notified.
        </div>
      )}

      {/* ── Hours a package includes (the Jersey package) ─────────────── */}
      {includedHours && (
        <div style={{ ...card, marginBottom: 12, padding: "12px 18px", fontSize: 13, color: theme.color.text, lineHeight: 1.5 }}>
          <strong>Your package includes {includedHours.thisYear} hours of consultancy this year</strong>
          {" "}({includedHours.byYear[0]} in year one, {includedHours.byYear[1]} in each of years two and three). Bookings within
          those hours are part of the package price; the rate card applies only to time beyond them.
        </div>
      )}

      {/* ── Rate card ──────────────────────────────────────────────────── */}
      <div style={{ ...card, marginBottom: 20, padding: "12px 18px", background: theme.color.panelAlt, display: "flex", gap: 24, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.06em" }}>{includedHours ? "Rate card beyond your package" : "Rate card"}</div>
        {GRADES.map((g) => {
          const r = data?.rateCard?.[g.id];
          if (!r) return null;
          return (
            <div key={g.id} style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: theme.color.text }}>{r.label}</span>
              <span style={{ fontSize: 12, color: theme.color.muted }}>£{(r.dayPence / 100).toLocaleString("en-GB")}/day</span>
              <span style={{ fontSize: 11, color: theme.color.faint }}>·</span>
              <span style={{ fontSize: 12, color: theme.color.muted }}>£{(r.hourPence / 100).toLocaleString("en-GB")}/hr</span>
            </div>
          );
        })}
      </div>

      {bookable && (<>
      {/* ── Calendar ───────────────────────────────────────────────────── */}
      <div style={{ ...card, marginBottom: 20 }}>

        {/* Month nav */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <button
            onClick={() => { setMonthOffset((o) => o - 1); setSelectedDate(null); }}
            style={{ background: "transparent", border: `1px solid ${theme.color.border}`, color: theme.color.muted, borderRadius: 3, padding: "5px 12px", cursor: "pointer", fontSize: 18, lineHeight: 1 }}
          >‹</button>
          <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading }}>
            {MONTH_NAMES[viewMon - 1]} {viewYear}
          </div>
          <button
            onClick={() => { setMonthOffset((o) => o + 1); setSelectedDate(null); }}
            style={{ background: "transparent", border: `1px solid ${theme.color.border}`, color: theme.color.muted, borderRadius: 3, padding: "5px 12px", cursor: "pointer", fontSize: 18, lineHeight: 1 }}
          >›</button>
        </div>

        {/* Day-of-week headers */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 4 }}>
          {DAY_LABELS.map((d) => (
            <div key={d} style={{ textAlign: "center", fontSize: 11, fontWeight: 700, color: theme.color.faint, padding: "4px 0" }}>{d}</div>
          ))}
        </div>

        {/* Day cells */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
          {cells.map((dateKey, i) => {
            if (!dateKey) return <div key={`e-${i}`} />;

            const dayNum    = parseInt(dateKey.slice(8), 10);
            const past      = dateKey < today;
            const isToday   = dateKey === today;
            const avail     = hasAnyCapacity(dateKey);
            const mySlots   = mySlotsByDate[dateKey] ?? [];
            const hasPending   = mySlots.some((s) => s.status === "pending");
            const hasConfirmed = mySlots.some((s) => s.status === "confirmed");
            const isSelected   = selectedDate === dateKey;
            const isWeekend    = i % 7 >= 5;
            // Weekends and firm holidays are closed; the server refuses them too.
            const holiday      = (data?.holidays ?? []).find((h) => h.date === dateKey) ?? null;
            const closed       = isWeekend || !!holiday;
            const canBook      = !past && !closed && avail;

            return (
              <div
                key={dateKey}
                onClick={() => canBook ? openDate(dateKey) : undefined}
                style={{
                  padding: "8px 4px",
                  borderRadius: 3,
                  textAlign: "center",
                  border: `1px solid ${isSelected ? theme.color.accent : isToday ? theme.color.borderStrong : theme.color.border}`,
                  background: isSelected ? theme.color.accentBg : "transparent",
                  cursor: canBook ? "pointer" : "default",
                  opacity: past ? 0.4 : 1,
                  transition: "border-color 0.1s, background 0.1s",
                  minHeight: 56,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: 4,
                }}
              >
                <div style={{
                  fontSize: 13,
                  fontWeight: isToday ? 800 : 600,
                  color: isSelected ? theme.color.accentText : isWeekend ? theme.color.faint : theme.color.text,
                }}>
                  {dayNum}
                </div>

                {/* Availability dot */}
                {!past && holiday && !hasPending && !hasConfirmed && (
                  <div title={holiday.reason ?? "Firm holiday"} style={{ fontSize: 9.5, fontWeight: 600, color: theme.color.faint }}>Closed</div>
                )}
                {!past && !closed && avail && !hasPending && !hasConfirmed && (
                  <div style={{ width: 5, height: 5, borderRadius: "50%", background: theme.color.accent, opacity: 0.8 }} />
                )}

                {/* Status dots */}
                <div style={{ display: "flex", gap: 3, justifyContent: "center" }}>
                  {hasPending   && <div style={{ width: 6, height: 6, borderRadius: "50%", background: theme.color.warning }} />}
                  {hasConfirmed && <div style={{ width: 6, height: 6, borderRadius: "50%", background: theme.color.success }} />}
                </div>
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div style={{ display: "flex", gap: 16, marginTop: 14, flexWrap: "wrap" }}>
          {[
            { dot: theme.color.accent,  label: "Available to book" },
            { dot: theme.color.warning, label: "My pending request" },
            { dot: theme.color.success, label: "My confirmed booking" },
          ].map(({ dot, label }) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: dot, flexShrink: 0 }} />
              <span style={{ fontSize: 11, color: theme.color.faint }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Booking panel (shown when a date is selected) ───────────────── */}
      {selectedDate && (() => {
        const dateLabel = new Date(selectedDate + "T00:00:00Z").toLocaleDateString("en-GB", {
          weekday: "long", day: "numeric", month: "long", year: "numeric",
        });
        const consultants = data?.consultants ?? [];

        return (
          <div style={{ ...card, marginBottom: 20, border: `1px solid ${theme.color.accent}` }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading }}>
                Request booking for {dateLabel}
              </div>
              <button onClick={() => setSelectedDate(null)} style={secondaryBtn}>Close</button>
            </div>

            {/* Step 1 — Consultant picker */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
                Choose a consultant
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {consultants.map((c) => {
                  const remaining = remainingHoursForConsultant(selectedDate, c.id);
                  const avail     = remaining > 0;
                  const active    = bookingConsultantId === c.id;
                  const gradeLabel = c.primaryGrade
                    ? (c.primaryGrade.charAt(0).toUpperCase() + c.primaryGrade.slice(1))
                    : "Consultant";
                  return (
                    <button
                      key={c.id}
                      onClick={() => avail && selectConsultant(c.id)}
                      disabled={!avail}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "10px 14px", borderRadius: 3, textAlign: "left",
                        border: `1px solid ${active ? theme.color.accent : theme.color.border}`,
                        background: active ? theme.color.accentBg : avail ? "transparent" : theme.color.panelAlt,
                        cursor: avail ? "pointer" : "not-allowed",
                        opacity: avail ? 1 : 0.45,
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: active ? theme.color.accentText : theme.color.heading }}>
                          {c.displayName}
                        </div>
                        <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>{gradeLabel}</div>
                      </div>
                      <div style={{ fontSize: 11, color: theme.color.faint, flexShrink: 0, marginLeft: 16 }}>
                        {avail ? `${remaining}h available` : "Unavailable this day"}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Step 2 — Grade + type + quantity (shown once a consultant is picked) */}
            {selectedConsultant && (() => {
              const grades = eligibleGrades(selectedConsultant);
              return (
                <>
                  {/* Grade selector */}
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
                      Type of work
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {grades.map((gId) => {
                        const g = GRADES.find((x) => x.id === gId)!;
                        const r = data?.rateCard?.[gId];
                        const active = bookingGrade === gId;
                        if (!r) return null;
                        return (
                          <button
                            key={gId}
                            onClick={() => setBookingGrade(gId)}
                            style={{
                              display: "flex", alignItems: "center", justifyContent: "space-between",
                              padding: "10px 14px", borderRadius: 3, textAlign: "left",
                              border: `1px solid ${active ? theme.color.accent : theme.color.border}`,
                              background: active ? theme.color.accentBg : "transparent",
                              cursor: "pointer",
                            }}
                          >
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: active ? theme.color.accentText : theme.color.heading }}>{r.label}</div>
                              <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>{g.description}</div>
                            </div>
                            {!isManagedService && (
                              <div style={{ fontSize: 11, color: theme.color.faint, flexShrink: 0, marginLeft: 16 }}>
                                £{(r.dayPence / 100).toLocaleString("en-GB")}/day · £{(r.hourPence / 100).toLocaleString("en-GB")}/hr
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Type + quantity */}
                  <div style={{ display: "flex", gap: 16, marginBottom: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Duration</div>
                      <div style={{ display: "flex", gap: 6 }}>
                        {(["day", "hour"] as const).map((t) => (
                          <button
                            key={t}
                            onClick={() => { setBookingType(t); setBookingQty(1); }}
                            style={{
                              padding: "7px 16px", borderRadius: 3, fontSize: 13, fontWeight: 600, cursor: "pointer",
                              border: `1px solid ${bookingType === t ? theme.color.accent : theme.color.border}`,
                              background: bookingType === t ? theme.color.accentBg : "transparent",
                              color: bookingType === t ? theme.color.accentText : theme.color.muted,
                            }}
                          >
                            {t === "day" ? "Full day" : "Hours"}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
                        {bookingType === "day" ? "Days" : "Hours"}
                      </div>
                      <select
                        value={bookingQty}
                        onChange={(e) => setBookingQty(Number(e.target.value))}
                        style={{ ...inputStyle, width: 80 }}
                      >
                        {Array.from({ length: maxQty }, (_, i) => i + 1).map((n) => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Note */}
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ fontSize: 12, color: theme.color.muted, display: "block", marginBottom: 5 }}>
                      Note <span style={{ color: theme.color.faint, fontWeight: 400 }}>(optional — describe what you need help with)</span>
                    </label>
                    <textarea
                      value={bookingNote}
                      onChange={(e) => setBookingNote(e.target.value)}
                      rows={2}
                      placeholder="e.g. Gap remediation for Annex A controls — A.8.8 and A.5.23"
                      style={{ ...inputStyle, width: "100%", boxSizing: "border-box", resize: "vertical", fontFamily: "inherit" }}
                    />
                  </div>

                  {/* Cost estimate + submit */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
                    {!isManagedService && (
                      <div>
                        <span style={{ fontSize: 22, fontWeight: 800, color: theme.color.accentText }}>
                          £{(estimatedCostPence / 100).toLocaleString("en-GB")}
                        </span>
                        <span style={{ fontSize: 12, color: theme.color.faint, marginLeft: 6 }}>estimated</span>
                      </div>
                    )}
                    <button
                      onClick={submitBooking}
                      disabled={submitting}
                      style={{ ...btnPrimary, opacity: submitting ? 0.55 : 1, cursor: submitting ? "default" : "pointer" }}
                    >
                      {submitting ? "Sending request…" : "Request booking"}
                    </button>
                  </div>

                  {submitError && (
                    <div style={{ marginTop: 10, fontSize: 12, color: theme.color.dangerText }}>{submitError}</div>
                  )}
                </>
              );
            })()}
          </div>
        );
      })()}

      </>)}

      {/* ── Pending requests ────────────────────────────────────────────── */}
      {pendingSlots.length > 0 && (
        <div style={{ ...card, marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 12 }}>
            Pending Requests
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {pendingSlots.map((s) => {
              const gradeLabel = data?.rateCard?.[s.grade]?.label ?? s.grade;
              const dateStr = new Date(s.slotDate).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
              const future = s.slotDate.slice(0, 10) >= today;
              return (
                <div key={s.id} style={{ padding: "10px 14px", background: "#78350f0d", border: `1px solid ${theme.color.warning}55`, borderRadius: 3, display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.text }}>
                      {gradeLabel} · {dateStr} · {hoursLabel(s.hours)}
                    </div>
                    {s.note && <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>{s.note}</div>}
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: theme.color.warningText, flexShrink: 0 }}>
                    Awaiting confirmation
                  </span>
                  {!isManagedService && <span style={{ fontSize: 13, fontWeight: 700, color: theme.color.accentText, flexShrink: 0 }}>{fmt(s.costPence)}</span>}
                  {future && (
                    <button onClick={() => cancelSlot(s.id)} style={secondaryBtn}>Cancel</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Confirmed upcoming ──────────────────────────────────────────── */}
      {confirmedUpcoming.length > 0 && (
        <div style={{ ...card, marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 12 }}>
            Confirmed Bookings
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {confirmedUpcoming.map((s) => {
              const gradeLabel = data?.rateCard?.[s.grade]?.label ?? s.grade;
              const dateStr = new Date(s.slotDate).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
              return (
                <div key={s.id} style={{ padding: "10px 14px", background: cbSafe("#052e160d"), border: `1px solid ${theme.color.success}55`, borderRadius: 3, display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.text }}>
                      {gradeLabel} · {dateStr} · {hoursLabel(s.hours)}
                    </div>
                    {s.note && <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>{s.note}</div>}
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: theme.color.successText, flexShrink: 0 }}>Confirmed</span>
                  {!isManagedService && <span style={{ fontSize: 13, fontWeight: 700, color: theme.color.accentText, flexShrink: 0 }}>{fmt(s.costPence)}</span>}
                  <button onClick={() => cancelSlot(s.id)} style={secondaryBtn}>Cancel</button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Booking history ─────────────────────────────────────────────── */}
      {historySlots.length > 0 && (
        <div style={card}>
          <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
            Booking history
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {historySlots.map((s) => {
              const gradeLabel = data?.rateCard?.[s.grade]?.label ?? s.grade;
              const dateStr = new Date(s.slotDate).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
              const { color, label } = slotStatusStyle(s.status);
              return (
                <div key={s.id} style={{ padding: "10px 14px", background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, borderRadius: 3, display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.text }}>
                      {gradeLabel} · {dateStr} · {hoursLabel(s.hours)}
                    </div>
                    {s.note && <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>{s.note}</div>}
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, color, flexShrink: 0 }}>{label}</span>
                  {!isManagedService && <span style={{ fontSize: 13, fontWeight: 700, color: theme.color.accentText, flexShrink: 0 }}>{fmt(s.costPence)}</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {pendingSlots.length === 0 && confirmedUpcoming.length === 0 && historySlots.length === 0 && (
        <div style={{ ...emptyFrame, fontSize: 13, color: theme.color.faint, padding: 32 }}>
          {bookable ? "No bookings yet — select a date above to request consultant time." : "No bookings yet."}
        </div>
      )}
    </div>
  );
}
